import { type FunctionComponent, type ReactElement, type JSX } from 'react';
import { useFragment } from 'react-relay';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import classnames from 'classnames';

// components
import { Hold } from './Hold.tsx';
import { PrivateOfferItemPrices } from './PrivateOfferItemPrices.tsx';
import { SaleItemPrices } from './SaleItemPrices.tsx';
import { NetItemPrices } from './NetItemPrices.tsx';
import { RetailItemPrices } from './RetailItemPrices.tsx';
import { PriceAmount } from './PriceAmount.tsx';
import { textPriceValue } from './messages/priceMessages.jsx';
import { LocalizedSoldIcon } from 'dibs-elements/exports/LocalizedSoldIcon.tsx';
import { LocalizedUnavailableIcon } from 'dibs-elements/exports/LocalizedUnavailableIcon.tsx';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';

import {
    PRICE_TYPES,
    TEXT_SIZE_SMALL,
    TEXT_SIZE_MEDIUM,
    TEXT_SIZE_LARGE_HEAVY,
    TEXT_SIZE_SMALL_HEAVY,
} from './constants.ts';

// helpers
import { getPriceFromAmountListByCurrency, isValidTextOnlyPriceType } from './helpers.ts';

// styles
import styles from './PriceDisplay.scss';

// types
import { type PriceDisplay_item$data as ItemType } from './__generated__/PriceDisplay_item.graphql.ts';
import { type PriceDisplay_sku$data as SkuType } from './__generated__/PriceDisplay_sku.graphql.ts';
import { type PriceDisplay_transaction$data as TransactionType } from './__generated__/PriceDisplay_transaction.graphql.ts';
import { type PriceDisplay_displayPrice$key } from './__generated__/PriceDisplay_displayPrice.graphql.ts';
import { type PriceDisplay_activeHold$key } from './__generated__/PriceDisplay_activeHold.graphql.ts';

type TextSize =
    | typeof TEXT_SIZE_SMALL
    | typeof TEXT_SIZE_MEDIUM
    | typeof TEXT_SIZE_LARGE_HEAVY
    | typeof TEXT_SIZE_SMALL_HEAVY;

type Props = {
    currency?: string;
    item: ItemType | null;
    sku?: SkuType | null;
    transaction?: TransactionType | null;
    textSize?: TextSize;
    useSvgIcon?: boolean;
    appendElementToPrice?: ReactElement;
    displayVatLabel?: boolean;
    vatLabelClassName?: string;
    showCompactedPrice?: boolean;
    showCompactedSalePrice?: boolean;
    hasPriceBlur?: boolean;
    showSoldOriginalPrice?: boolean;
    showSoldListPrice?: boolean;
    showNetRetailPrice?: boolean;
    showLabelOnNewLine?: boolean;
    overridePrice?: number;
    overrideIsVatIncluded?: boolean;
    overridePriceBookName?: string;
    addCurrencyDataAttr?: boolean;
};

const displayPriceFragment = graphql`
    fragment PriceDisplay_displayPrice on ItemDisplayPriceType {
        textType
    }
`;

const activeHoldFragment = graphql`
    fragment PriceDisplay_activeHold on Hold {
        displayTimeToExpire
    }
`;

export const usePriceLabel = ({
    displayPrice: displayPriceRef,
    textSize,
    useSvgIcon,
}: {
    displayPrice: PriceDisplay_displayPrice$key | null | undefined;
    textSize?: TextSize;
    useSvgIcon?: boolean;
}): JSX.Element | null => {
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);
    const textType = displayPrice?.textType || '';

    if (!isValidTextOnlyPriceType(textType)) {
        return null;
    }

    return (
        <div
            className={classnames({
                [styles.sold]: textType === PRICE_TYPES.SOLD,
                [styles.unavailable]: textType === PRICE_TYPES.UNAVAILABLE,
                [styles.pur]: textType === PRICE_TYPES.PUR,
            })}
            data-tn={`price-${textType === PRICE_TYPES.SOLD ? 'DLOS' : textType}-price`}
        >
            {(() => {
                if (useSvgIcon && [PRICE_TYPES.SOLD, PRICE_TYPES.UNAVAILABLE].includes(textType)) {
                    const className = classnames(styles.icon, {
                        [styles.smallText]: textSize === TEXT_SIZE_SMALL,
                    });

                    if (textType === PRICE_TYPES.SOLD) {
                        return (
                            <ClientSuspense fallback={<div className={className} />}>
                                <LocalizedSoldIcon className={className} />
                            </ClientSuspense>
                        );
                    } else if (textType === PRICE_TYPES.UNAVAILABLE) {
                        return (
                            <ClientSuspense fallback={<div className={className} />}>
                                <LocalizedUnavailableIcon className={className} />
                            </ClientSuspense>
                        );
                    }
                }

                // most basic case - return label as text
                return textPriceValue(textType);
            })()}
        </div>
    );
};

export const useHoldLabel = ({
    displayPrice: displayPriceRef,
    activeHold: activeHoldRef,
    showCompactedPrice,
}: {
    displayPrice: PriceDisplay_displayPrice$key | null | undefined;
    activeHold: PriceDisplay_activeHold$key | null | undefined;
    showCompactedPrice?: boolean;
}): JSX.Element | null => {
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);
    const activeHold = useFragment(activeHoldFragment, activeHoldRef);
    const textType = displayPrice?.textType || '';
    const holdDisplayTime = activeHold?.displayTimeToExpire || null;
    const onHold = textType === PRICE_TYPES.HOLD || textType === PRICE_TYPES.HOLD_CUSTOM;
    if (!onHold) {
        return null;
    }

    return (
        <Hold
            holdType={textType}
            holdDisplayTime={holdDisplayTime}
            showCompactedPrice={showCompactedPrice}
        />
    );
};

export const PriceDisplayComponent: FunctionComponent<Props> = ({
    item,
    sku,
    transaction,
    currency = 'USD',
    textSize = TEXT_SIZE_MEDIUM,
    useSvgIcon = false,
    appendElementToPrice,
    displayVatLabel = false,
    vatLabelClassName,
    showCompactedPrice = false,
    showCompactedSalePrice = false,
    hasPriceBlur = false,
    showSoldOriginalPrice = true,
    showSoldListPrice = true,
    showNetRetailPrice = true,
    showLabelOnNewLine = false,
    overridePrice,
    overrideIsVatIncluded,
    overridePriceBookName,
    addCurrencyDataAttr,
}) => {
    const activeHold = item?.activeHold;
    const holdDisplayTime = activeHold?.displayTimeToExpire || null;
    const itemPriceDisplay = sku ? sku.displayPrice?.[0] : item?.displayPrice?.[0];
    const priceLabel = usePriceLabel({ displayPrice: itemPriceDisplay, textSize, useSvgIcon });
    const holdLabel = useHoldLabel({
        displayPrice: itemPriceDisplay,
        activeHold,
        showCompactedPrice,
    });

    if (!itemPriceDisplay) {
        return <div className={styles.wrapper} />;
    }

    const {
        convertedAmountList,
        originalConvertedAmountList,
        privateOfferConvertedAmountList,
        amountType,
        textType,
        quantityDisplay,
        percentageOff,
        privateOfferPercentageOff,
        showPriceLabel,
        offerExpirationDate,
        isVatIncluded: isVatIncludedFromItem,
        priceBookName: priceBookNameFromItem,
    } = itemPriceDisplay;

    const {
        isVatOrder,
        priceBookName: priceBookNameFromTransaction,
        mostRecentOffer,
        summary,
    } = transaction || {};

    const isVatIncluded = overrideIsVatIncluded ?? isVatOrder ?? isVatIncludedFromItem;
    const priceBookName =
        overridePriceBookName ?? priceBookNameFromTransaction ?? priceBookNameFromItem;

    const price =
        overridePrice ?? (getPriceFromAmountListByCurrency(convertedAmountList, currency) || 0);
    const originalPrice =
        getPriceFromAmountListByCurrency(originalConvertedAmountList, currency) || 0;

    const percentageValue = percentageOff ? parseInt(percentageOff) : undefined;
    const privateOfferPercentageValue = privateOfferPercentageOff
        ? parseInt(privateOfferPercentageOff)
        : undefined;
    const isPriceValid = typeof price === 'number' && price > 0;
    const shouldDisplayVatLabel = !!displayVatLabel && !!isVatIncluded;

    // Getting priceComponent
    let priceComponent = null;
    const sharedPriceProps = {
        price,
        originalPrice,
        currency,
        quantityDisplay,
        hasPrivateOffer: !!privateOfferConvertedAmountList,
    };

    if (isValidTextOnlyPriceType(textType)) {
        const isSold = textType === PRICE_TYPES.SOLD;
        // Item is unavailable or has no visible price (e.g. sold, unavailable or pur)
        priceComponent = (
            <div className={styles.soldUnavailablePriceWrapper}>
                {priceLabel}
                {!showCompactedPrice &&
                    (!!price && !!originalPrice && isSold ? (
                        <>
                            {/* SOLD PRICE */}
                            <PriceAmount
                                isInline
                                type={PRICE_TYPES.RETAIL}
                                currency={currency}
                                amount={price}
                                quantityDisplay={quantityDisplay}
                                appendElementToPrice={appendElementToPrice}
                                priceBookName={priceBookName}
                                hasPriceBlur={hasPriceBlur}
                            />
                            {/* ORIGINAL PRICE */}
                            {showSoldOriginalPrice &&
                                (showSoldListPrice || originalPrice > price) && (
                                    <PriceAmount
                                        hasSalePrice
                                        showLabel
                                        currency={currency}
                                        amount={originalPrice}
                                        quantityDisplay={quantityDisplay}
                                        hasPrivateOffer={!!privateOfferConvertedAmountList}
                                        priceBookName={priceBookName}
                                    />
                                )}
                        </>
                    ) : (
                        (!!originalPrice || !!price) &&
                        textType !== PRICE_TYPES.PUR && (
                            <PriceAmount
                                isInline
                                type={
                                    amountType === PRICE_TYPES.FINAL &&
                                    //fallback to retail price if item has only original price
                                    !(amountType === PRICE_TYPES.FINAL && !!originalPrice)
                                        ? PRICE_TYPES.FINAL
                                        : PRICE_TYPES.RETAIL
                                }
                                currency={currency}
                                amount={originalPrice || price}
                                showLabel
                                showLabelOnNewLine={showLabelOnNewLine}
                                quantityDisplay={quantityDisplay}
                                isUnpurchasable
                                displayVatLabel={shouldDisplayVatLabel}
                                vatLabelClassName={vatLabelClassName}
                                priceBookName={priceBookName}
                            />
                        )
                    ))}
            </div>
        );
    } else if (isPriceValid && !isValidTextOnlyPriceType(textType)) {
        if (amountType === PRICE_TYPES.SALE) {
            // Item is on sale
            priceComponent = (
                <SaleItemPrices
                    {...sharedPriceProps}
                    percentageOff={percentageValue}
                    appendElementToPrice={appendElementToPrice}
                    displayVatLabel={shouldDisplayVatLabel}
                    vatLabelClassName={vatLabelClassName}
                    priceBookName={priceBookName}
                    showCompactedPrice={showCompactedPrice || showCompactedSalePrice}
                    addCurrencyDataAttr={addCurrencyDataAttr}
                />
            );
        } else if (amountType === PRICE_TYPES.NET) {
            // Item has a net price and is being viewed by a trade user
            priceComponent = (
                <NetItemPrices
                    {...sharedPriceProps}
                    percentageOff={percentageValue}
                    displayVatLabel={shouldDisplayVatLabel}
                    vatLabelClassName={vatLabelClassName}
                    priceBookName={priceBookName}
                    showCompactedPrice={showCompactedPrice}
                    showNetRetailPrice={showNetRetailPrice}
                />
            );
        } else if (amountType === PRICE_TYPES.RETAIL) {
            // Item only has a retail price or is being viewed by a non-trade user or it's private offer original price
            priceComponent = (
                <RetailItemPrices
                    {...sharedPriceProps}
                    percentageOff={privateOfferPercentageValue}
                    showPriceLabel={!!showPriceLabel}
                    appendElementToPrice={appendElementToPrice}
                    displayVatLabel={shouldDisplayVatLabel}
                    vatLabelClassName={vatLabelClassName}
                    priceBookName={priceBookName}
                    addCurrencyDataAttr={addCurrencyDataAttr}
                />
            );
        }
    }

    // Items can have holds, but not valid price e.g. PUR items
    if (!priceComponent && !holdLabel) {
        return null;
    }

    let privateOffer;
    if (privateOfferConvertedAmountList) {
        const offerAmountFromTransaction = mostRecentOffer?.totalAmount?.convertedAmountList;
        const offerAmountList = offerAmountFromTransaction || privateOfferConvertedAmountList;
        const privateOfferPrice = getPriceFromAmountListByCurrency(offerAmountList, currency);

        privateOffer = (
            <PrivateOfferItemPrices
                {...sharedPriceProps}
                price={privateOfferPrice}
                expiration={offerExpirationDate}
                showExpirationDate={!holdDisplayTime}
                displayVatLabel={shouldDisplayVatLabel}
                vatLabelClassName={vatLabelClassName}
                priceBookName={priceBookName}
            />
        );
        if (transaction) {
            priceComponent = (
                <RetailItemPrices
                    {...sharedPriceProps}
                    price={getPriceFromAmountListByCurrency(
                        summary?.adjustedItemPrice?.totalAmount?.convertedAmountList || null,
                        currency
                    )}
                    percentageOff={privateOfferPercentageValue}
                    showPriceLabel={!!showPriceLabel}
                    appendElementToPrice={appendElementToPrice}
                    displayVatLabel={shouldDisplayVatLabel}
                    vatLabelClassName={vatLabelClassName}
                    priceBookName={priceBookName}
                />
            );
        }
    }

    const priceWrapperClasses = classnames(styles.wrapper, {
        [styles.smallText]: textSize === TEXT_SIZE_SMALL,
        [styles.smallHeavy]: textSize === TEXT_SIZE_SMALL_HEAVY,
        [styles.largeHeavy]: textSize === TEXT_SIZE_LARGE_HEAVY,
    });

    if (holdLabel && showCompactedPrice) {
        privateOffer = null;
        priceComponent = null;
    }

    return (
        <div className={priceWrapperClasses} data-tn="price-wrapper">
            {/* 'On Hold For You' OR 'On Hold' */}
            {holdLabel}
            {/* show Private Offer if exists */}
            {privateOffer}
            {/* Price */}
            {priceComponent}
        </div>
    );
};

export const PriceDisplay = createFragmentContainer(PriceDisplayComponent, {
    item: graphql`
        fragment PriceDisplay_item on Item
        @argumentDefinitions(
            userId: { type: "String", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
            userCountryCode: { type: "String" }
            priceBookName: { type: "String" }
            catchNonFatalErrors: { type: "Boolean", defaultValue: false }
        ) {
            activeHold {
                displayTimeToExpire
                ...PriceDisplay_activeHold
            }
            displayPrice(
                isTrade: $isTrade
                buyerId: $userId
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
                catchNonFatalErrors: $catchNonFatalErrors
            ) {
                originalConvertedAmountList {
                    currency
                    amount
                }
                convertedAmountList {
                    currency
                    amount
                }
                privateOfferConvertedAmountList {
                    currency
                    amount
                }
                quantityDisplay
                percentageOff
                privateOfferPercentageOff
                textType
                amountType
                showPriceLabel
                offerExpirationDate
                isVatIncluded
                priceBookName
                ...PriceDisplay_displayPrice
            }
        }
    `,
    sku: graphql`
        fragment PriceDisplay_sku on ItemSkuType
        @argumentDefinitions(
            hasSkuId: { type: "Boolean", defaultValue: false }
            userId: { type: "String", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
            userCountryCode: { type: "String" }
            priceBookName: { type: "String" }
            catchNonFatalErrors: { type: "Boolean", defaultValue: false }
        ) {
            ... @include(if: $hasSkuId) {
                displayPrice(
                    isTrade: $isTrade
                    buyerId: $userId
                    userCountryCode: $userCountryCode
                    priceBookName: $priceBookName
                    catchNonFatalErrors: $catchNonFatalErrors
                ) {
                    originalConvertedAmountList {
                        currency
                        amount
                    }
                    convertedAmountList {
                        currency
                        amount
                    }
                    privateOfferConvertedAmountList {
                        currency
                        amount
                    }
                    quantityDisplay
                    percentageOff
                    privateOfferPercentageOff
                    textType
                    amountType
                    showPriceLabel
                    offerExpirationDate
                    isVatIncluded
                    priceBookName
                    ...PriceDisplay_displayPrice
                }
            }
        }
    `,
    transaction: graphql`
        fragment PriceDisplay_transaction on Transaction {
            mostRecentOffer {
                totalAmount {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
            summary {
                adjustedItemPrice {
                    totalAmount {
                        convertedAmountList {
                            amount
                            currency
                        }
                    }
                }
            }
            isVatOrder
            priceBookName
        }
    `,
});
